import _ from 'lodash';
import type { HTMLAttributes } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import classNames from 'classnames';
import type { To } from 'react-router-dom';
import useEventListener from '../utils/useEventListener';
import { useRouter } from '../utils/router/v3Bridge';
import * as styles from './DropdownMenu.module.scss';
import addTestId from '../utils/addTestId';
import Button from './Button';
import HotkeyInfo from './HotkeyInfo';

export type DropdownMenuItemType<Data> = {
  id: string;
  className?: string;
  link?: To;
  data: Data;
  hotkeyAction?: string;
};

export type RenderItemFn<Data> = (
  data: Data,
  options: { id: string; focused?: boolean },
) => React.ReactNode;

interface DropdownMenuItemProps
  extends Pick<
    HTMLAttributes<HTMLElement>,
    'onClick' | 'onMouseEnter' | 'onMouseDown'
  > {
  focused?: boolean;
  className?: string;
  children: React.ReactNode;
  to?: To;
  hotkeys?: Set<string>;
  hotkeyAction?: string;
}

const Item = React.forwardRef(
  (
    {
      focused,
      onClick,
      onMouseEnter,
      onMouseDown,
      children,
      className,
      to,
      hotkeys,
      hotkeyAction,
    }: DropdownMenuItemProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    return (
      <Button
        ref={ref}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseDown={onMouseDown}
        className={classNames(styles.item, className, {
          [styles.focused]: focused,
        })}
        role="menuitem"
        to={to}
      >
        {children}
        {hotkeys && focused && (
          <div className={styles.hotkeyInfoContainer}>
            <HotkeyInfo keys={hotkeys} action={hotkeyAction} />
          </div>
        )}
      </Button>
    );
  },
);

export type DropdownMenuSection<Data> = {
  label?: string;
  items: DropdownMenuItemType<Data>[];
  className?: string;
};

export const getItemFromSectionsByIndex = <Data,>(
  sections: DropdownMenuSection<Data>[],
  index: number,
) => {
  return _.flatten(sections.map(({ items }) => items))[index];
};

interface SectionHeaderProps {
  children: React.ReactNode;
  button?: React.ReactNode;
}

const SectionHeader = ({ children, button }: SectionHeaderProps) => {
  return (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionHeaderLabel}>{children}</div>
      {!!button && <div>{button}</div>}
    </div>
  );
};

interface UseDropdownEventListenerProps {
  inputElement: HTMLElement | null;
  index: number | null;
  onIndexChange(index: number): void;
  onSelect(): void;
  onEscape?: () => void;
  canTabToSelect?: boolean;
  totalItems: number;
}

export const useDropdownEventListener = ({
  inputElement,
  onSelect,
  onIndexChange,
  onEscape,
  canTabToSelect,
  index,
  totalItems,
}: UseDropdownEventListenerProps) => {
  const [hasInteractedWithKeyboard, setHasInteractedWithKeyboard] =
    useState(false);

  useEventListener(inputElement, 'keydown', (event) => {
    if (canTabToSelect && event.key === 'Tab') {
      event.preventDefault();
      onSelect();
      return;
    }

    switch (event.key) {
      case 'Escape':
        onEscape?.();
        break;

      case 'ArrowUp':
        setHasInteractedWithKeyboard(true);
        event.preventDefault();
        if (index == null) {
          onIndexChange(totalItems - 1);
        } else if (index > 0) {
          onIndexChange(index - 1);
        }
        break;

      case 'ArrowDown':
        setHasInteractedWithKeyboard(true);
        event.preventDefault();
        if (index == null) {
          onIndexChange(0);
        } else if (index < totalItems - 1) {
          onIndexChange(index + 1);
        }
        break;

      case 'Enter': {
        setHasInteractedWithKeyboard(true);
        event.preventDefault();
        onSelect();
        break;
      }
      default:
        break;
    }
  });

  return { hasInteractedWithKeyboard };
};

interface UseScrollItemIntoViewProps {
  index: number | null;
  containerRef: React.RefObject<HTMLDivElement>;
  focusedItemRef: React.RefObject<HTMLButtonElement>;
}

export const useScrollItemIntoView = ({
  index,
  containerRef,
  focusedItemRef,
}: UseScrollItemIntoViewProps) => {
  const lastIndex = useRef<number | null>(null);
  useEffect(() => {
    if (lastIndex.current != null && focusedItemRef.current) {
      scrollIntoView(focusedItemRef.current, {
        boundary: containerRef.current,
        block: index && index > lastIndex.current ? 'end' : 'start',
        scrollMode: 'if-needed',
      });
    }

    lastIndex.current = index;
  }, [index, containerRef, focusedItemRef]);
};

const canTabToSelect = (item: DropdownMenuItemType<unknown> | null) =>
  item?.link == null;

interface DropdownMenuProps<Section, Data> {
  sections: Section[];
  index: number | null;
  onIndexChange(index: number): void;
  onSelect?: (item: DropdownMenuItemType<Data>) => void;
  onClose(): void;
  inputContainer: HTMLElement | null;
  maxHeight?: string | number;
  renderItem: RenderItemFn<Data>;
  emptyState?: React.ReactNode;
  testId?: string;
  hotkeysEnabled?: boolean;
}

const Root = <Data,>({
  sections,
  index,
  onIndexChange,
  onSelect,
  onClose,
  inputContainer,
  maxHeight,
  renderItem,
  emptyState,
  testId,
  hotkeysEnabled,
}: DropdownMenuProps<DropdownMenuSection<Data>, Data>) => {
  const router = useRouter();

  const focusedItemRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useScrollItemIntoView({
    index,
    containerRef,
    focusedItemRef,
  });

  const startIndexes = sections.reduce(
    (indexes, current) => [
      ...indexes,
      (_.last(indexes) ?? 0) + current.items.length,
    ],
    [0],
  );
  const totalItems = _.last(startIndexes) ?? 0;

  const currentItem =
    index != null ? getItemFromSectionsByIndex(sections, index) : null;

  useDropdownEventListener({
    inputElement: inputContainer,
    index,
    totalItems,
    onIndexChange,
    onSelect() {
      if (currentItem) {
        if (onSelect) {
          onSelect(currentItem);
        }
        if (currentItem.link) {
          router.push(currentItem.link);
        }
      }
    },
    onEscape: onClose,
    canTabToSelect:
      hotkeysEnabled && !!currentItem && canTabToSelect(currentItem),
  });

  return (
    <div
      className={styles.dropdown}
      style={maxHeight ? { maxHeight } : undefined}
      ref={containerRef}
      {...addTestId(testId)}
    >
      {totalItems === 0
        ? emptyState || (
            <div className={styles.emptyMessage}>No results found</div>
          )
        : sections.map(({ label, items, className }, sectionIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={sectionIndex} className={className}>
              {label && <SectionHeader>{label}</SectionHeader>}
              {items.map((item, itemIndex) => {
                const itemGlobalIndex =
                  (startIndexes[sectionIndex] ?? 0) + itemIndex;
                const isFocused = index === itemGlobalIndex;

                const hotkeys = (() => {
                  if (hotkeysEnabled) {
                    return canTabToSelect(item)
                      ? new Set(['Enter', 'Tab'])
                      : new Set(['Enter']);
                  }
                  return undefined;
                })();

                return (
                  <Item
                    key={item.id}
                    focused={isFocused}
                    onMouseEnter={() => onIndexChange(itemGlobalIndex)}
                    onClick={() => {
                      if (onSelect) {
                        onSelect(item);
                      }
                    }}
                    ref={isFocused ? focusedItemRef : undefined}
                    className={item.className}
                    hotkeyAction={item.hotkeyAction}
                    to={item.link}
                    hotkeys={hotkeys}
                  >
                    {renderItem(item.data, {
                      id: item.id,
                      focused: isFocused,
                    })}
                  </Item>
                );
              })}
            </div>
          ))}
    </div>
  );
};

const DropdownMenu = {
  Item,
  SectionHeader,
  Root,
};

export default DropdownMenu;
